import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { Download, Eye, FileCode, Mail } from "lucide-react";
import { api } from "@/lib/apiClient";
import { canCancelInvoice, canWriteInvoice } from "@/lib/permissions";
import {
  canShowInvoicePdf,
  fetchDunningPdfPreview,
  fetchInvoicePdf,
  fetchInvoiceXrechnung,
} from "@/lib/invoicePdf";
import { formatCurrency } from "@/lib/money";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { isInvoiceFinalized } from "@/lib/invoiceStatus";
import { AddInvoiceLineModal } from "@/components/invoices/AddInvoiceLineModal";
import { CreateCreditNoteModal } from "@/components/invoices/CreateCreditNoteModal";
import { InvoiceCreditPayoutSection } from "@/components/invoices/InvoiceCreditPayoutSection";
import { InvoiceLinesEditor } from "@/components/invoices/InvoiceLinesEditor";
import { InvoicePaymentsSection } from "@/components/invoices/InvoicePaymentsSection";
import { canAddInvoiceLine } from "@/lib/invoiceLineEdit";
import { InvoiceTypeBadge } from "@/components/invoices/InvoiceTypeBadge";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { StatusBadge } from "@/components/ui/StatusBadge";
import type { Booking, Invoice, InvoiceListItem } from "@/types/camping";

export interface InvoiceDetailModalProps {
  invoice: InvoiceListItem | null;
  open: boolean;
  onClose: () => void;
  userGroups: string[];
  onChanged?: () => void;
}

function amountNumber(value: string | null | undefined): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function SummaryTile({
  label,
  value,
  hint,
  tone = "default",
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "default" | "attention" | "danger";
}) {
  return (
    <div className="rounded-xl bg-fill-tertiary/80 px-3 py-2.5">
      <p className="text-[0.6875rem] font-medium uppercase tracking-wide text-label-secondary">
        {label}
      </p>
      <p
        className={clsx(
          "mt-0.5 text-base font-semibold tabular-nums tracking-tight text-label sm:text-lg",
          tone === "attention" && "text-status-orange",
          tone === "danger" && "text-status-red",
        )}
      >
        {value}
      </p>
      {hint ? <p className="mt-0.5 text-xs leading-snug text-label-tertiary">{hint}</p> : null}
    </div>
  );
}

export function InvoiceDetailModal({
  invoice,
  open,
  onClose,
  userGroups,
  onChanged,
}: InvoiceDetailModalProps) {
  const canInvoice = canWriteInvoice(userGroups);
  const canStorno = canCancelInvoice(userGroups);
  const [detail, setDetail] = useState<Invoice | null>(null);
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<string | null>(null);
  const [stornoReason, setStornoReason] = useState("");
  const [showStorno, setShowStorno] = useState(false);
  const [addLineOpen, setAddLineOpen] = useState(false);
  const [creditNoteOpen, setCreditNoteOpen] = useState(false);
  const [includeXrechnung, setIncludeXrechnung] = useState(false);

  const loadDetail = useCallback(() => {
    if (!invoice?.id || !open) return;
    setLoading(true);
    setError(null);
    api
      .get<Invoice>(`/api/v1/invoices/${invoice.id}`)
      .then((inv) => {
        setDetail(inv);
        if (inv.booking_id) {
          api
            .get<Booking>(`/api/v1/bookings/${inv.booking_id}`)
            .then(setBooking)
            .catch(() => setBooking(null));
        } else {
          setBooking(null);
        }
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [invoice?.id, open]);

  useEffect(() => {
    if (open && invoice) {
      loadDetail();
      setShowStorno(false);
      setStornoReason("");
    } else {
      setDetail(null);
    }
  }, [open, invoice, loadDetail]);

  const inv = detail ?? invoice;
  if (!inv) return null;

  const listItem = invoice;

  const canDun =
    canInvoice &&
    (listItem?.is_dunnable ??
      (inv.status === "issued" &&
        inv.open_amount != null &&
        parseFloat(inv.open_amount) > 0 &&
        inv.due_date != null &&
        inv.due_date < new Date().toISOString().slice(0, 10) &&
        (inv.dunning_level ?? 0) < 3));

  async function sendDunning() {
    if (
      !window.confirm(
        `Mahnung (Stufe ${(inv!.dunning_level ?? 0) + 1}) per E-Mail an den Gast senden?`
      )
    ) {
      return;
    }
    setAction("dunning");
    setError(null);
    try {
      await api.post(`/api/v1/invoices/${inv!.id}/send-dunning`, {});
      loadDetail();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Mahnung konnte nicht gesendet werden.");
    } finally {
      setAction(null);
    }
  }

  async function openDunningPdf() {
    setAction("dunning-pdf");
    setError(null);
    try {
      await fetchDunningPdfPreview(inv!.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Mahn-PDF konnte nicht geladen werden.");
    } finally {
      setAction(null);
    }
  }

  async function sendInvoiceEmail() {
    setAction("email");
    setError(null);
    try {
      await api.post(`/api/v1/invoices/${inv!.id}/send-email`, {
        include_xrechnung: includeXrechnung,
      });
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "E-Mail konnte nicht gesendet werden.");
    } finally {
      setAction(null);
    }
  }

  async function openPdf(mode: "preview" | "download") {
    setAction(`pdf-${mode}`);
    setError(null);
    try {
      await fetchInvoicePdf(inv!.id, inv!.invoice_number, mode);
    } catch (e) {
      setError(e instanceof Error ? e.message : "PDF konnte nicht geladen werden.");
    } finally {
      setAction(null);
    }
  }

  async function downloadXrechnung() {
    setAction("xrechnung");
    setError(null);
    try {
      await fetchInvoiceXrechnung(inv!.id, inv!.invoice_number);
    } catch (e) {
      setError(e instanceof Error ? e.message : "X-Rechnung konnte nicht geladen werden.");
    } finally {
      setAction(null);
    }
  }

  async function cancelInvoice() {
    const reason = stornoReason.trim();
    if (reason.length < 3) {
      setError("Stornogrund muss mindestens 3 Zeichen haben.");
      return;
    }
    setAction("cancel");
    setError(null);
    try {
      await api.post<Invoice>(`/api/v1/invoices/${inv!.id}/cancel`, { reason });
      setShowStorno(false);
      setStornoReason("");
      loadDetail();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Storno fehlgeschlagen.");
    } finally {
      setAction(null);
    }
  }

  const guestLabel = listItem?.guest_name ?? "—";
  const bookingNr = listItem?.booking_number;
  const isCreditNote = inv.invoice_type === "credit_note";
  const paidOnInvoice = Number(inv.paid_amount ?? 0);
  const openAmount = amountNumber(inv.open_amount);
  const taxAmount = amountNumber(inv.tax_amount);
  const canCreateCreditNote =
    canInvoice &&
    !isCreditNote &&
    isInvoiceFinalized(inv.status) &&
    Number.isFinite(paidOnInvoice) &&
    paidOnInvoice > 0;
  const showDocuments = canShowInvoicePdf(inv.status);
  const showStornoAction = canStorno && isInvoiceFinalized(inv.status) && !isCreditNote;
  const overdue = listItem?.is_overdue ?? false;
  const dunningLevel = inv.dunning_level ?? 0;

  const openTone =
    inv.open_amount == null || openAmount <= 0
      ? "default"
      : overdue
        ? "danger"
        : "attention";

  let dueHint: string | undefined;
  if (dunningLevel > 0) {
    dueHint = `Mahnstufe ${dunningLevel}`;
    if (inv.last_dunned_at) {
      dueHint += ` · ${formatIsoDateShort(inv.last_dunned_at.slice(0, 10))}`;
    }
  }

  const documentFooter: ReactNode = showDocuments ? (
    <div className="space-y-2.5">
      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {inv.has_stored_pdf || isInvoiceFinalized(inv.status) ? (
        <p className="text-xs text-label-tertiary">Hinterlegtes PDF, unveränderbar.</p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Eye size={15} aria-hidden />}
          loading={action === "pdf-preview"}
          disabled={!!action}
          onClick={() => void openPdf("preview")}
        >
          PDF anzeigen
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<Download size={15} aria-hidden />}
          loading={action === "pdf-download"}
          disabled={!!action}
          onClick={() => void openPdf("download")}
        >
          PDF speichern
        </Button>
        {isInvoiceFinalized(inv.status) ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leftIcon={<FileCode size={15} aria-hidden />}
            loading={action === "xrechnung"}
            disabled={!!action}
            onClick={() => void downloadXrechnung()}
          >
            X-Rechnung
          </Button>
        ) : null}
        {canInvoice ? (
          <Button
            type="button"
            size="sm"
            leftIcon={<Mail size={15} aria-hidden />}
            loading={action === "email"}
            disabled={!!action}
            onClick={() => void sendInvoiceEmail()}
          >
            Per E-Mail senden
          </Button>
        ) : null}
      </div>
      {canInvoice && isInvoiceFinalized(inv.status) ? (
        <label className="flex items-start gap-2 text-xs text-label-secondary">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={includeXrechnung}
            onChange={(e) => setIncludeXrechnung(e.target.checked)}
            disabled={!!action}
          />
          <span>X-Rechnung (XML) mitsenden. Das PDF ist immer eine ZUGFeRD-Rechnung.</span>
        </label>
      ) : null}
    </div>
  ) : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={inv.invoice_number}
      description={guestLabel}
      size="lg"
      footer={documentFooter}
    >
      <AddInvoiceLineModal
        open={addLineOpen}
        invoice={detail}
        onClose={() => setAddLineOpen(false)}
        onAdded={() => {
          loadDetail();
          onChanged?.();
        }}
      />
      <CreateCreditNoteModal
        open={creditNoteOpen}
        sourceInvoice={detail}
        onClose={() => setCreditNoteOpen(false)}
        onCreated={() => {
          onChanged?.();
        }}
      />
      <div className="space-y-5">
        {!showDocuments && error ? <p className="text-sm text-status-red">{error}</p> : null}

        {canDun && !isCreditNote ? (
          <Alert
            variant={overdue ? "error" : "warning"}
            title={`Mahnung Stufe ${dunningLevel + 1} möglich`}
          >
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                loading={action === "dunning"}
                disabled={!!action}
                onClick={() => void sendDunning()}
              >
                Mahnung senden
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={action === "dunning-pdf"}
                disabled={!!action}
                onClick={() => void openDunningPdf()}
              >
                Vorschau
              </Button>
            </div>
          </Alert>
        ) : null}

        {inv.cancellation_reason ? (
          <Alert variant="error" title="Storniert">
            {inv.cancellation_reason}
          </Alert>
        ) : null}

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <SummaryTile
            label="Gesamt"
            value={formatCurrency(inv.total_amount, inv.currency)}
            hint={taxAmount > 0 ? `inkl. ${formatCurrency(inv.tax_amount, inv.currency)} MwSt.` : undefined}
          />
          <SummaryTile
            label={isCreditNote ? "Auszuzahlen" : "Offen"}
            value={
              inv.open_amount != null
                ? formatCurrency(inv.open_amount, inv.currency)
                : "—"
            }
            hint={
              paidOnInvoice > 0
                ? `${isCreditNote ? "Ausgezahlt" : "Bezahlt"} ${formatCurrency(inv.paid_amount ?? "0", inv.currency)}`
                : undefined
            }
            tone={openTone}
          />
          <SummaryTile
            label="Ausgestellt"
            value={inv.issue_date ? formatIsoDateShort(inv.issue_date) : "—"}
          />
          <SummaryTile
            label="Fällig"
            value={inv.due_date ? formatIsoDateShort(inv.due_date) : "—"}
            hint={dueHint}
            tone={overdue ? "danger" : "default"}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <InvoiceTypeBadge invoiceType={inv.invoice_type} />
          <StatusBadge status={inv.status} kind="invoice" />
          {overdue ? (
            <span className="rounded-full bg-status-red/15 px-2.5 py-0.5 text-xs font-medium text-status-red">
              Überfällig
            </span>
          ) : null}
          {bookingNr ? (
            inv.booking_id ? (
              <Link
                to="/bookings"
                className="font-mono text-xs text-accent hover:underline"
                onClick={onClose}
              >
                Buchung {bookingNr}
              </Link>
            ) : (
              <span className="font-mono text-xs text-label-secondary">Buchung {bookingNr}</span>
            )
          ) : null}
          {inv.reference_invoice_number ? (
            <span className="text-xs text-label-secondary">
              Bezug{" "}
              <span className="font-mono text-label">{inv.reference_invoice_number}</span>
            </span>
          ) : null}
          {loading ? <span className="text-xs text-label-tertiary">Aktualisiere…</span> : null}
        </div>

        {inv.notes ? (
          <p className="rounded-lg bg-fill-tertiary/80 px-3 py-2 text-sm text-label-secondary">
            {inv.notes}
          </p>
        ) : null}

        <section>
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-label">Positionen</h3>
            {canInvoice && detail && canAddInvoiceLine(detail) ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!!action || loading}
                onClick={() => setAddLineOpen(true)}
              >
                Position hinzufügen
              </Button>
            ) : null}
          </div>
          {detail && inv.lines.length > 0 ? (
            <InvoiceLinesEditor
              invoice={detail}
              booking={booking}
              hideHeading
              disabled={!!action || loading}
              onChanged={() => {
                loadDetail();
                onChanged?.();
              }}
            />
          ) : (
            <p className="mt-2 text-sm text-label-tertiary">
              {loading ? "Positionen werden geladen…" : "Keine Positionen."}
            </p>
          )}
        </section>

        {isCreditNote ? (
          <InvoiceCreditPayoutSection
            invoice={inv}
            canRecord={canInvoice}
            showSummary={false}
            onPayoutRecorded={() => {
              loadDetail();
              onChanged?.();
            }}
          />
        ) : (
          <InvoicePaymentsSection
            invoice={inv}
            canRecord={canInvoice}
            showSummary={false}
            onPaymentRecorded={() => {
              loadDetail();
              onChanged?.();
            }}
          />
        )}

        {canCreateCreditNote || showStornoAction ? (
          <div className="border-t border-separator pt-4">
            <p className="text-sm font-semibold text-label">Weitere Aktionen</p>
            <div className="mt-3 space-y-3">
              {canCreateCreditNote ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!!action || loading}
                  onClick={() => setCreditNoteOpen(true)}
                >
                  Gutschrift erstellen
                </Button>
              ) : null}
              {showStornoAction ? (
                showStorno ? (
                  <div className="space-y-2">
                    <label className="block text-xs text-label-secondary">
                      Stornogrund (Pflichtfeld)
                      <textarea
                        className="eh-input mt-1 min-h-[3rem] w-full resize-y text-sm"
                        value={stornoReason}
                        onChange={(e) => setStornoReason(e.target.value)}
                        rows={2}
                      />
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        loading={action === "cancel"}
                        disabled={!!action}
                        onClick={() => void cancelInvoice()}
                      >
                        Storno bestätigen
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={!!action}
                        onClick={() => setShowStorno(false)}
                      >
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={!!action}
                    onClick={() => setShowStorno(true)}
                  >
                    Rechnung stornieren
                  </Button>
                )
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
